import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TableSortLabel,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Clear,
  Business,
  Visibility,
  Security
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { Organization, PermissionGroup } from '../api/BackofficeApi';

type SortField = 'name' | 'tokens';
type SortDirection = 'asc' | 'desc';

interface ColumnFilter {
  name: string;
  tokens: string;
}

const Organizations: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  
  // Form state for add/edit organization
  const [formData, setFormData] = useState({
    name: '',
    tokens: 0,
    permissionsGroup: [] as string[]
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [saving, setSaving] = useState(false);
  
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Column filters state
  const [columnFilters, setColumnFilters] = useState<ColumnFilter>({
    name: '',
    tokens: ''
  });
  
  // Filter menu state
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<keyof ColumnFilter | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [orgs, groups] = await Promise.all([
        authAPI.getOrganizations(),
        authAPI.getPermissionGroups()
      ]);
      
      setOrganizations(orgs);
      setPermissionGroups(groups);
    } catch (err: any) {
      setError('Failed to load organizations');
      console.error('Organizations error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Sorting function
  const sortOrganizations = (organizations: Organization[]): Organization[] => {
    return [...organizations].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      // Handle number sorting for tokens
      if (sortField === 'tokens') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
      
      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Column filtering function
  const applyColumnFilters = (organizations: Organization[]): Organization[] => {
    return organizations.filter(organization => {
      const nameMatch = !columnFilters.name || 
        organization.name.toLowerCase().includes(columnFilters.name.toLowerCase());
      const tokensMatch = !columnFilters.tokens || 
        organization.tokens.toString().includes(columnFilters.tokens);
      
      return nameMatch && tokensMatch;
    });
  };

  const filteredOrganizations = applyColumnFilters(sortOrganizations(organizations.filter(organization => {
    const matchesSearch = organization.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  })));

  const handleEditOrganization = (organization: Organization) => {
    setSelectedOrganization(organization);
    setEditMode(true);
    setFormData({
      name: organization.name,
      tokens: organization.tokens,
      permissionsGroup: organization.permissionsGroup.map(group => group.name)
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleAddOrganization = () => {
    setSelectedOrganization(null);
    setEditMode(false);
    setFormData({
      name: '',
      tokens: 0,
      permissionsGroup: []
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Organization name is required';
    }
    
    if (formData.tokens < 0) {
      errors.tokens = 'Tokens must be a positive number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveOrganization = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      
      // Get selected permission groups
      const selectedGroups = permissionGroups.filter(group => 
        formData.permissionsGroup.includes(group.name)
      );
      
      const organizationData: Organization = {
        name: formData.name,
        tokens: formData.tokens,
        permissionsGroup: selectedGroups
      };
      
      if (editMode && selectedOrganization) {
        // Update existing organization
        await authAPI.updateOrganization(selectedOrganization.name, organizationData);
      } else {
        // Create new organization
        await authAPI.createOrganization(organizationData);
      }
      
      // Refresh the organizations list
      await fetchData();
      
      // Close dialog and reset form
      setDialogOpen(false);
      setSelectedOrganization(null);
      setFormData({
        name: '',
        tokens: 0,
        permissionsGroup: []
      });
      setFormErrors({});
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save organization');
      console.error('Save organization error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrganization = async (organization: Organization) => {
    if (!window.confirm(`Are you sure you want to delete organization "${organization.name}"?`)) {
      return;
    }
    
    try {
      await authAPI.deleteOrganization(organization.name);
      await fetchData(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete organization');
      console.error('Delete organization error:', err);
    }
  };


  // Sorting handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter handlers
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, column: keyof ColumnFilter) => {
    setFilterMenuAnchor(event.currentTarget);
    setActiveFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterMenuAnchor(null);
    setActiveFilterColumn(null);
  };

  const handleColumnFilterChange = (column: keyof ColumnFilter, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const clearColumnFilter = (column: keyof ColumnFilter) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: ''
    }));
  };

  const clearAllFilters = () => {
    setColumnFilters({
      name: '',
      tokens: ''
    });
  };

  const handleOrganizationClick = (organization: Organization) => {
    navigate(`/organizations/${organization.name}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Organization Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddOrganization}
        >
          Add Organization
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              onClick={clearAllFilters}
              startIcon={<Clear />}
              fullWidth
              disabled={Object.values(columnFilters).every(filter => !filter)}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Organization Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Organizations
              </Typography>
              <Typography variant="h4">
                {organizations.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tokens
              </Typography>
              <Typography variant="h4">
                {organizations.reduce((sum, org) => sum + org.tokens, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Permission Groups
              </Typography>
              <Typography variant="h4">
                {permissionGroups.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Organization Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Organization Name
                  </TableSortLabel>
                  <Box display="flex" alignItems="center">
                    {columnFilters.name && (
                      <Chip
                        size="small"
                        label={columnFilters.name}
                        onDelete={() => clearColumnFilter('name')}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'name')}
                      color={columnFilters.name ? 'primary' : 'default'}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <TableSortLabel
                    active={sortField === 'tokens'}
                    direction={sortField === 'tokens' ? sortDirection : 'asc'}
                    onClick={() => handleSort('tokens')}
                  >
                    Tokens
                  </TableSortLabel>
                  <Box display="flex" alignItems="center">
                    {columnFilters.tokens && (
                      <Chip
                        size="small"
                        label={columnFilters.tokens}
                        onDelete={() => clearColumnFilter('tokens')}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'tokens')}
                      color={columnFilters.tokens ? 'primary' : 'default'}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>Permission Groups</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrganizations.map((organization) => (
              <TableRow key={organization.name}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Business sx={{ mr: 1, color: 'text.secondary' }} />
                    <Button
                      variant="text"
                      onClick={() => handleOrganizationClick(organization)}
                      sx={{ 
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        p: 0,
                        minWidth: 'auto',
                        textAlign: 'left'
                      }}
                    >
                      {organization.name}
                    </Button>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={organization.tokens}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {organization.permissionsGroup.map((group, index) => (
                      <Chip
                        key={index}
                        label={group.name}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOrganizationClick(organization)}
                    title="View Organization Details"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEditOrganization(organization)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteOrganization(organization)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Organization' : 'Add Organization'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Organization Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                disabled={editMode}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tokens"
                type="number"
                value={formData.tokens}
                onChange={(e) => handleFormChange('tokens', parseInt(e.target.value) || 0)}
                error={!!formErrors.tokens}
                helperText={formErrors.tokens}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Permission Groups</InputLabel>
                <Select
                  multiple
                  value={formData.permissionsGroup}
                  onChange={(e) => handleFormChange('permissionsGroup', e.target.value)}
                  input={<OutlinedInput label="Permission Groups" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {permissionGroups.map((group) => (
                    <MenuItem key={group.name} value={group.name}>
                      <Checkbox checked={formData.permissionsGroup.indexOf(group.name) > -1} />
                      <ListItemText primary={group.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveOrganization} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : (editMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={handleFilterClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {activeFilterColumn && (
          <>
            <MenuItem disabled>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Filter by {activeFilterColumn}
              </Typography>
            </MenuItem>
            <MenuItem>
              <TextField
                fullWidth
                size="small"
                placeholder={`Filter ${activeFilterColumn}...`}
                value={columnFilters[activeFilterColumn]}
                onChange={(e) => handleColumnFilterChange(activeFilterColumn, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </MenuItem>
            {columnFilters[activeFilterColumn] && (
              <MenuItem onClick={() => clearColumnFilter(activeFilterColumn)}>
                <Box display="flex" alignItems="center">
                  <Clear sx={{ mr: 1 }} />
                  Clear Filter
                </Box>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </Box>
  );
};

export default Organizations;
